import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DOCUMENT,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    inject,
} from '@angular/core';
import { EnvironmentService } from '@core/services/environment.service';

export enum Stops {
    FALLBACK = 'FALLBACK',
    BASE = '0px',
    SM = '640px',
    MD = '768px',
    LG = '1024px',
    XL = '1280px',
    XXL = '1536px',
}

export enum StopsMediaQuery {
    BASE = '(max-width: 639.9px)',
    SM = '(min-width: 640px) and (max-width: 767.9px)',
    MD = '(min-width: 768px) and (max-width: 1023.9px)',
    LG = '(min-width: 1024px) and (max-width: 1279.9px)',
    XL = '(min-width: 1280px) and (max-width: 1535.9px)',
    XXL = '(min-width: 1536px) ',
}

export const StopsMediaQueryMapping = {
    [Stops.FALLBACK]: StopsMediaQuery.BASE,
    [Stops.BASE]: StopsMediaQuery.BASE,
    [Stops.SM]: StopsMediaQuery.SM,
    [Stops.MD]: StopsMediaQuery.MD,
    [Stops.LG]: StopsMediaQuery.LG,
    [Stops.XL]: StopsMediaQuery.XL,
    [Stops.XXL]: StopsMediaQuery.XXL,
};

export interface ImageConfiguration {
    /** 1x URL; also the default `<img>` source. Densities are prebuilt — the
     *  renderer appends no query parameters. */
    src: string;
    src2x?: string;
    src3x?: string;
    lqip?: string;
    alt?: string;
}
export type Configurations = Partial<Record<Stops, ImageConfiguration>>;

export interface Source {
    srcSet: string;
    defaultSrc: string;
    mediaQuery: string;
    stop: Stops;
}

@Component({
    selector: 'app-lazy-picture',
    templateUrl: './lazy-picture.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass],
})
export class LazyPictureComponent implements OnChanges, OnInit {
    private cd = inject(ChangeDetectorRef);
    private environmentService = inject(EnvironmentService);
    private doc = inject<Document>(DOCUMENT);

    @Input() configurations: Configurations;
    @Input() alt = '';
    @Input() additionalClasses: string[] | string = [];
    @Input() lazy = true;
    @Input() preload = false;

    sources: Source[] = [];
    sourcesMap: Partial<Record<Stops, Source>> = {};
    fallback: Source;
    stops = Stops;
    trackBy(index: number, item: Source) {
        return item.mediaQuery;
    }

    cssClasses: string[] = [];

    generateSource(stop: Stops, configuration: ImageConfiguration): Source {
        let [srcSet, defaultSrc] = ['', ''];
        if (configuration.src) {
            [srcSet, defaultSrc] = this.generateSrcSet(configuration);
        }
        return {
            stop: stop,
            srcSet: srcSet,
            defaultSrc: defaultSrc,
            mediaQuery: StopsMediaQueryMapping[stop],
        };
    }

    generateSrcSet(configuration: ImageConfiguration): [string, string] {
        const srcSet = [
            `${configuration.src} 1x`,
            configuration.src2x && `${configuration.src2x} 2x`,
            configuration.src3x && `${configuration.src3x} 3x`,
        ]
            .filter(Boolean)
            .join(', ');
        return [srcSet, configuration.lqip || configuration.src];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.configurations && changes.configurations.currentValue) {
            const configurationStops = Object.keys(this.configurations);
            const fakeFallback = configurationStops.includes(Stops.FALLBACK)
                ? {}
                : { [Stops.FALLBACK]: { src: '' } };
            const { FALLBACK, ...configurations } = {
                ...fakeFallback,
                ...this.configurations,
            };
            this.fallback = this.generateSource(Stops.FALLBACK, FALLBACK);
            this.sources = Object.entries(configurations)
                .filter(([stop, configuration]) => !!configuration.src)
                .map(([stop, configuration]) =>
                    this.generateSource(stop as Stops, configuration)
                );
            this.sources.sort(
                (a, b) => +b.stop.slice(0, -2) - +a.stop.slice(0, -2)
            );
            this.sourcesMap = Object.assign(
                {},
                ...this.sources.map(source => ({ [source.stop]: source }))
            );
            if (this.preload) {
                this.generatePreloadLinks();
            }
            this.cd.detectChanges();
        }
        if (changes.additionalClasses || changes.lazy) {
            this.cssClasses = (this.lazy ? ['lazyload'] : []).concat(
                this.additionalClasses
            );
            this.cd.detectChanges();
        }
    }

    generatePreloadLinks() {
        for (const value in Stops) {
            const source = this.sourcesMap[Stops[value]];
            if (source) {
                this.createPreloadLink(
                    source.defaultSrc,
                    source.srcSet,
                    Stops[value],
                    StopsMediaQuery[value]
                );
            }
        }
    }

    createPreloadLink(
        url: string,
        srcSet: string,
        key: string,
        mediaQuery: string
    ) {
        // only create preload links on the server, since it will be too late on the client side anyway.
        if (
            !this.doc.getElementById(key) &&
            this.environmentService.isServer()
        ) {
            const link: HTMLLinkElement = this.doc.createElement('link');
            link.setAttribute('rel', 'preload');
            link.setAttribute('href', url);
            link.setAttribute('id', key);
            link.setAttribute('media', mediaQuery);
            link.setAttribute('as', 'image');
            link.setAttribute('imagesrcset', srcSet);
            if (!this.lazy) {
                link.setAttribute('fetchpriority', 'high');
            }
            // preload lcp image as early as possible
            this.doc.getElementById('viewport-definition').after(link);
        }
    }

    ngOnInit(): void {
        this.cssClasses = (this.lazy ? ['lazyload'] : []).concat(
            this.additionalClasses
        );
        this.cd.detectChanges();
    }
}
